import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Brain } from 'lucide-react';
import { useHeroImage } from '@/hooks/useHeroImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  { key: 'basic', label: 'BASIC PRACTICE', description: 'Foundational concepts and practice' },
  { key: 'comps_ready', label: "COMP'S READY", description: '25-question timed tests • Comprehensive coverage • Board exam prep' },
  { key: 'advanced', label: 'ADVANCED', description: 'Advanced practice and mastery' }
];

export default function MemoryGameCenter() {
  const [gamesByTierAndCategory, setGamesByTierAndCategory] = useState({});
  const [testCategories, setTestCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const heroImage = useHeroImage('memory_game_center');
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load both memory games and test categories
      const [gamesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/memory-games`),
        axios.get(`${API}/test-categories`) // Public endpoint
      ]);
      
      // Set categories
      setTestCategories(categoriesRes.data.filter(cat => cat.enabled));
      
      // Group memory games by tier and category
      const grouped = {};
      gamesRes.data.forEach(game => {
        const tier = game.tier;
        const cat = game.category;
        if (!grouped[tier]) {
          grouped[tier] = {};
        }
        if (!grouped[tier][cat]) {
          grouped[tier][cat] = [];
        }
        grouped[tier][cat].push(game);
      });
      
      setGamesByTierAndCategory(grouped);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (tier, categorySlug) => {
    const games = gamesByTierAndCategory[tier]?.[categorySlug] || [];
    if (games.length > 0) {
      navigate(`/memory-game-category/${tier}/${categorySlug}`);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <div 
        className="relative bg-cover bg-center h-[400px] mb-12 flex items-center justify-center"
        style={heroImage ? { backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${heroImage})` } : { background: 'linear-gradient(135deg, #FF6B9D 0%, #C44569 100%)' }}
      >
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl retro-title text-[#F4E4C1] mb-4">
            MEMORY GAME
          </h1>
          <p className="text-xl text-[#F4E4C1] max-w-3xl mx-auto">
            Match terms & definitions • Test your recall • Beat your best time
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 pb-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B5A3C] mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-16">
            {TIERS.map(tier => {
              // Get categories for this tier
              const tierCategories = testCategories.filter(cat => cat.tier === tier.key);
              
              if (tierCategories.length === 0) return null;

              return (
                <div key={tier.key}>
                  {/* Tier Header */}
                  <div className="bg-gradient-to-r from-[#8B5A3C] to-[#6B4423] text-white py-8 px-8 rounded-lg mb-8 shadow-lg">
                    <h2 className="text-3xl md:text-4xl font-bold retro-title mb-2">{tier.label}</h2>
                    <p className="text-lg opacity-90">{tier.description}</p>
                  </div>

                  {/* Categories Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {tierCategories.map(category => {
                      const gamesInCategory = gamesByTierAndCategory[tier.key]?.[category.slug] || [];
                      const gameCount = gamesInCategory.length;

                      return (
                        <div
                          key={category.id}
                          onClick={() => handleCategoryClick(tier.key, category.slug)}
                          className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer border-4 border-[#8B5A3C] hover:border-[#FF6B9D] transform hover:scale-105"
                        >
                          <div className="bg-gradient-to-br from-[#FF6B9D] to-[#C44569] p-6 text-white">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-4xl">{category.icon}</span>
                              <Brain className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-bold retro-title">{category.name}</h3>
                          </div>
                          <div className="p-6">
                            <p className="text-gray-600 mb-4">{category.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-[#8B5A3C]">
                                {gameCount} {gameCount === 1 ? 'Game' : 'Games'} Available
                              </span>
                              {gameCount > 0 && (
                                <span className="text-[#FF6B9D] font-bold">Play →</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && testCategories.length === 0 && (
          <div className="text-center py-16">
            <Brain className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-xl text-gray-600">No memory games available yet.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
